/* assets/cbi-cabinet.js
 * cbi-vimeo-bg: fills in the iframe src once the page itself has finished
 * loading, so the clip is already running by the time anyone scrolls to it
 * (Kurt).
 *
 * Rendering the src server side would put a Vimeo player download and a
 * connection to player.vimeo.com in the critical path of every homepage load.
 * The iframe ships with src="" and this element fills it in after the load
 * event, in an idle callback, so it costs the page nothing on the way in. It
 * used to wait for the band to come into view, which meant the clip started
 * as the visitor arrived at it rather than before.
 *
 * The src is never cleared again: tearing a playing background video down and
 * rebuilding it is worse than leaving it running.
 */

const VIMEO_PARAMS = [
  'background=1',
  'autoplay=1',
  'muted=1',
  'loop=1',
  'autopause=0',
  'controls=0',
  'title=0',
  'byline=0',
  'portrait=0',
  'dnt=1',
].join('&');

class CbiVimeoBg extends HTMLElement {
  connectedCallback() {
    this.iframe = this.querySelector('iframe');
    this.videoId = (this.dataset.vimeoId || '').trim();

    if (!this.iframe || !/^\d+$/.test(this.videoId)) {
      /* No usable ID. Leave the poster showing and do nothing else. */
      this.hidden = true;
      return;
    }

    if (this.dataset.designMode === 'true') {
      /* Load straight away in the theme editor so the merchant can see the clip
       * while they work, without having to scroll the preview to it. */
      this.load();
      return;
    }

    this.whenIdle(() => this.load());
  }

  /* After the load event, and then on the first idle moment, with a timeout so
   * a busy page still gets there. Nothing here is on the critical path. */
  whenIdle(run) {
    const queue = () => {
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 3000 });
      else window.setTimeout(run, 200);
    };
    if (document.readyState === 'complete') queue();
    else window.addEventListener('load', queue, { once: true });
  }

  load() {
    if (this.loaded) return;
    this.loaded = true;
    this.iframe.src = `https://player.vimeo.com/video/${this.videoId}?${VIMEO_PARAMS}`;
    this.dataset.state = 'loaded';
    if (this.dataset.videoRatio === 'auto') this.detectRatio();
  }

  /* The client's clip shape is not known in advance. Vimeo's oEmbed endpoint is
   * CORS enabled and returns the clip's width and height; the ratio feeds the
   * cover maths in cbi-cabinet.css. On any failure the CSS default of 16:9
   * stays, and the Video shape setting can override it. */
  detectRatio() {
    const url = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${this.videoId}`)}`;
    fetch(url, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const width = data && Number(data.width);
        const height = data && Number(data.height);
        if (width > 0 && height > 0) {
          this.style.setProperty('--cbi-video-ratio', (width / height).toFixed(4));
        }
      })
      .catch(() => {
        /* Keep the 16:9 default. */
      });
  }
}

if (!customElements.get('cbi-vimeo-bg')) {
  customElements.define('cbi-vimeo-bg', CbiVimeoBg);
}
